import gsap from "gsap";
import { scroller, Link } from "react-scroll";
import { useSelector } from 'react-redux';
import { useLanguage, getTaricJsonErrorsTranslation } from '../../hooks/useLanguage';
import { extractFieldName } from '../../utils/fieldIdUtils';
import { readRowIndex, readRowDeclarationErrors } from '../../utils/declarationKindUtils';

/** Matches `id` on ReviewReceiverDetails form groups (exportNumber is 1-based). */
const getReviewReceiverFieldId = (field, exportNumber) => {
  const n = Number(exportNumber);
  if (!Number.isFinite(n) || n < 1) return `receiver_${field}`;
  return n === 1 ? `receiver_${field}` : `receiver_${field}_${n}`;
};

export const TaricJsonErrors = ({ taricJsonErrors, location, onExpandExport, isLoading = false }) => {
    const [isSpanish] = useLanguage();
    // Get declarations from Redux to match errors to correct export numbers
    const declarations = useSelector((state) => state.preparedJson?.declarations || []);
   
    const handleErrorFieldClick = (e, field) => {
        e.preventDefault(); 
        
        const element = document.getElementById(field);
        if (element) {
          scroller.scrollTo(field, {
            duration: 100,      
            offset: -300,      
            smooth: 'easeInOutQuint',
            containerId: 'analysis-report'
          });
      
          setTimeout(() => {
            element.classList.add('highlight-error');
            
            setTimeout(() => {
              element.classList.remove('highlight-error');
            }, 5000);
          }, 300); 
        }
      };

    const handleClickWithExpand = (e, fieldId, declIndex) => {
      const shouldExpand =
        typeof declIndex === 'number' && declIndex >= 0 && typeof onExpandExport === 'function';
      if (shouldExpand) {
        onExpandExport(declIndex);
      }
      if (
        typeof fieldId === 'string' &&
        (fieldId.startsWith('receiver_') || fieldId.startsWith('sender_'))
      ) {
        window.dispatchEvent(new CustomEvent('expandReviewUserDetailsCompany'));
      }
      const runScroll = () => handleErrorFieldClick(e, fieldId);
      // Allow Dashboard to re-render expanded export before scrolling to the field
      if (shouldExpand) {
        window.setTimeout(runScroll, 220);
      } else {
        runScroll();
      }
    };

    // Function to get field display name based on field name and language
    const getFieldDisplayName = (fieldName) => {
      const fieldMappings = {
        // Declaration fields
        expediente: !isSpanish ? "Declaration" : "Expediente",
        tipo: !isSpanish ? "Type" : "Tipo",
        fecha_registro: !isSpanish ? "Registration Date" : "Fecha Registro",
        fecha_creacion: !isSpanish ? "Creation Date" : "Fecha Creación",
        cliente_id: !isSpanish ? "Client ID" : "Cliente ID",
        agente_id: !isSpanish ? "Agent ID" : "Agente ID",
        remitente_id: !isSpanish ? "Sender ID" : "Remitente ID",
        declaracion_1: !isSpanish ? "Declaration 1" : "Declaración 1",
        declaracion_2: !isSpanish ? "Declaration 2" : "Declaración 2",
        declaracion_3: !isSpanish ? "Declaration 3" : "Declaración 3",
        aduana_entrada: !isSpanish ? "Entry Customs" : "Aduana Entrada",
        aduana_salida: !isSpanish ? "Exit Customs" : "Aduana Salida",
        aduana_despacho: !isSpanish ? "Clearance Customs" : "Aduana Despacho",
        
        // Item fields
        descripcion_mercancia: !isSpanish ? "Goods Description" : "Descripción de Mercancía",
        codigo_nc: !isSpanish ? "NC Code" : "Código NC",
        masa_bruta: !isSpanish ? "Gross Weight" : "Masa Bruta",
        masa_neta: !isSpanish ? "Net Weight" : "Masa Neta",
        pais_origen: !isSpanish ? "Country of Origin" : "País de Origen",
        cantidad_euros: !isSpanish ? "Euro Amount" : "Cantidad en Euros",
        precio: !isSpanish ? "Price" : "Precio",
        provincia_origen: !isSpanish ? "Province of Origin" : "Provincia de Origen",
        regimen_solicitado_precedente: !isSpanish ? "Previous Regime" : "Régimen Solicitado",
        valor_estadistico: !isSpanish ? "Statistical Value" : "Valor Estadístico",
        // Import-specific fields
        metodo_valoracion_aduana: !isSpanish ? "Customs Valuation Method" : "Método Valoración Aduana",
        cantidad_divisas: !isSpanish ? "Foreign Currency Amount" : "Cantidad en Divisas",
        regimen_comunitario_nacional: !isSpanish ? "Community/National Regime" : "Régimen Comunitario/Nacional",
        avales: !isSpanish ? "Guarantees" : "Avales",
        dv1: !isSpanish ? "Valuation Declaration (DV1)" : "Declaración de Valor (DV1)",
        tributos: !isSpanish ? "Taxes" : "Tributos",
        referencias_fiscales: !isSpanish ? "Fiscal References" : "Referencias Fiscales",
        documentos_precedentes: !isSpanish ? "Preceding Documents" : "Documentos Precedentes",


        // Document fields
        codigo_documento: !isSpanish ? "Document Code" : "Código de Documento",
        numero: !isSpanish ? "Number" : "Número",
        fecha: !isSpanish ? "Date" : "Fecha",
        
        // Package fields
        cantidad: !isSpanish ? "Quantity" : "Cantidad",
        cantidad_restante: !isSpanish ? "Remaining Quantity" : "Cantidad Restante",
        piezas: !isSpanish ? "Pieces" : "Piezas",
        es_interno_o_externo: !isSpanish ? "Internal/External" : "Interno/Externo",
        marca: !isSpanish ? "Mark" : "Marca",
        
        // Company/Agent fields
        nombre: !isSpanish ? "Name" : "Nombre",
        nif: !isSpanish ? "Tax ID" : "NIF",
        localidad: !isSpanish ? "City" : "Localidad",
        pais: !isSpanish ? "Country" : "País",
        identificacion_fiscal: !isSpanish ? "Tax Identification" : "Identificación Fiscal",
        
        // Transport and other fields
        pais_destino: !isSpanish ? "Destination Country" : "País Destino",
        pais_exportacion: !isSpanish ? "Export Country" : "País Exportación",
        pais_transporte_frontera: !isSpanish ? "Border Transport Country" : "País Transporte Frontera",
        modo_transporte_frontera: !isSpanish ? "Border Transport Mode" : "Modo Transporte Frontera",
        medio_transporte_frontera: !isSpanish ? "Border Transport Means" : "Medio Transporte Frontera",
        modo_transporte_interior: !isSpanish ? "Internal Transport Mode" : "Modo Transporte Interior",
        identidad_transporte_frontera: !isSpanish ? "Border Transport Identity" : "Identidad Transporte Frontera",
        codigo_un: !isSpanish ? "UN Code" : "Código UN",
        factura: !isSpanish ? "Invoice" : "Factura",
        fecha_factura: !isSpanish ? "Invoice Date" : "Fecha Factura",
        condiciones_entrega: !isSpanish ? "Delivery Terms" : "Condiciones Entrega",
        naturaleza_transaccion: !isSpanish ? "Transaction Nature" : "Naturaleza Transacción",
        observaciones: !isSpanish ? "Observations" : "Observaciones",
        tiene_contenedores: !isSpanish ? "Has Containers" : "Tiene Contenedores",
        total_bultos: !isSpanish ? "Total Packages" : "Total Bultos",
        estado_edi: !isSpanish ? "EDI Status" : "Estado EDI",
        estado_edi_canarias: !isSpanish ? "Canary Islands EDI Status" : "Estado EDI Canarias"
      };
      
      return fieldMappings[fieldName] || fieldName;
    };

    // Field name extraction utility is now imported from fieldIdUtils.js

    // Convert error object to new format
    const convertErrorsToNewFormat = (errors) => {
      if (!errors || typeof errors !== 'object') return {};
      
      const converted = {};
      Object.entries(errors).forEach(([field, error]) => {
        // Skip if the error value is an object (like nested documentos, embalajes)
        if (typeof error === 'object' && error !== null) return;
        
        converted[field] = {
          field: field,
          errorField: getFieldDisplayName(field),
          errorMessage: error
        };
      });
      return converted;
    };

    // Helper function to check if an object has any direct string/primitive errors
    const hasDirectErrors = (obj) => {
      if (!obj || typeof obj !== 'object') return false;
      return Object.values(obj).some(value => typeof value === 'string' || typeof value === 'number');
    };

    // Helper function to check if partida has any errors (direct or nested)
    const partidaHasErrors = (partida) => {
      if (!partida || typeof partida !== 'object') return false;
      
      // Check direct errors
      if (hasDirectErrors(partida)) return true;
      
      // Check documentos errors (filter out null values)
      if (partida.documentos && Array.isArray(partida.documentos)) {
        // Check if any documento in the array has errors (skip null entries)
        const hasDocumentoErrors = partida.documentos.some(doc => {
          return doc !== null && typeof doc === 'object' && hasDirectErrors(doc);
        });
        if (hasDocumentoErrors) return true;
      }
      
      // Check embalajes errors (filter out null values)
      if (partida.embalajes && Array.isArray(partida.embalajes)) {
        // Check if any embalaje in the array has errors (skip null entries)
        const hasEmbalajeErrors = partida.embalajes.some(emb => {
          return emb !== null && typeof emb === 'object' && hasDirectErrors(emb);
        });
        if (hasEmbalajeErrors) return true;
      }
      
      return false;
    };

    // Check if this is the new error format with errors array
    const isNewErrorFormat = taricJsonErrors && 
      typeof taricJsonErrors === 'object' && 
      !Array.isArray(taricJsonErrors) &&
      Array.isArray(taricJsonErrors.errors) &&
      taricJsonErrors.errors.length > 0;

    // Transform new error format to old format for compatibility
    let processedErrors = taricJsonErrors;
    if (isNewErrorFormat) {
      // Transform the new format to the old format structure
      const transformed = {
        common: {},
        exportaciones: []
      };
      
      taricJsonErrors.errors.forEach((errorObj) => {
        // Validate and get declaration index (ensure it's a non-negative number).
        // Accepts export (exportacionesIndex) or import (importacionesIndex) rows.
        const rawExportIndex = readRowIndex(errorObj);
        const exportIndex = (rawExportIndex !== undefined && typeof rawExportIndex === 'number' && rawExportIndex >= 0)
          ? rawExportIndex
          : 0;
        
        // Ensure exportaciones array is large enough for this export index
        while (transformed.exportaciones.length <= exportIndex) {
          transformed.exportaciones.push({});
        }
        
        // Initialize export if it doesn't exist
        if (!transformed.exportaciones[exportIndex]) {
          transformed.exportaciones[exportIndex] = {};
        }
        
        // Store the export number (exportacionesIndex + 1) for reference
        transformed.exportaciones[exportIndex]._exportNumber = exportIndex + 1;
        
        // Handle clientes errors - associate them with the specific export, not common
        if (errorObj.clientes) {
          // Store clientes errors in the export object, not in common
          if (!transformed.exportaciones[exportIndex].clientes) {
            transformed.exportaciones[exportIndex].clientes = {};
          }
          Object.assign(transformed.exportaciones[exportIndex].clientes, errorObj.clientes);
        }
        
        // Handle declaration-level errors (export or import kind)
        const declErrors = readRowDeclarationErrors(errorObj);
        if (declErrors) {
          // Handle declaration-level errors (direct fields)
          Object.keys(declErrors).forEach(key => {
            if (key !== 'partidas') {
              transformed.exportaciones[exportIndex][key] = declErrors[key];
            }
          });

          // Handle partidas errors
          if (declErrors.partidas && Array.isArray(declErrors.partidas)) {
            if (!transformed.exportaciones[exportIndex].partidas) {
              transformed.exportaciones[exportIndex].partidas = [];
            }

            declErrors.partidas.forEach((partidaError, pIndex) => {
              // Process partida errors - handle both direct errors and nested errors (documentos/embalajes)
              if (partidaError !== null && typeof partidaError === 'object') {
                // Ensure partidas array is large enough
                while (transformed.exportaciones[exportIndex].partidas.length <= pIndex) {
                  transformed.exportaciones[exportIndex].partidas.push(null);
                }
                
                // CRITICAL: Always create the partida object if it doesn't exist This ensures partidas with only nested errors (documentos/embalajes) are preserved Even if the partida has no direct errors, we need the object to store nested errors
                if (!transformed.exportaciones[exportIndex].partidas[pIndex]) {
                  transformed.exportaciones[exportIndex].partidas[pIndex] = {};
                }
                
                // Handle partida-level errors (direct fields)
                Object.keys(partidaError).forEach(key => {
                  if (key !== 'embalajes' && key !== 'documentos') {
                    transformed.exportaciones[exportIndex].partidas[pIndex][key] = partidaError[key];
                  }
                });
                
                // Handle embalajes errors
                if (partidaError.embalajes && Array.isArray(partidaError.embalajes)) {
                  if (!transformed.exportaciones[exportIndex].partidas[pIndex].embalajes) {
                    transformed.exportaciones[exportIndex].partidas[pIndex].embalajes = [];
                  }
                  
                  partidaError.embalajes.forEach((embalajeError, eIndex) => {
                    if (embalajeError !== null && typeof embalajeError === 'object') {
                      while (transformed.exportaciones[exportIndex].partidas[pIndex].embalajes.length <= eIndex) {
                        transformed.exportaciones[exportIndex].partidas[pIndex].embalajes.push(null);
                      }
                      // Merge embalaje errors if there's already an object at this index
                      if (transformed.exportaciones[exportIndex].partidas[pIndex].embalajes[eIndex] && 
                          typeof transformed.exportaciones[exportIndex].partidas[pIndex].embalajes[eIndex] === 'object') {
                        Object.assign(transformed.exportaciones[exportIndex].partidas[pIndex].embalajes[eIndex], embalajeError);
                      } else {
                        transformed.exportaciones[exportIndex].partidas[pIndex].embalajes[eIndex] = embalajeError;
                      }
                    }
                  });
                }
                
                // Handle documentos errors
                if (partidaError.documentos && Array.isArray(partidaError.documentos)) {
                  // Ensure documentos array exists - this is critical for partidas with only documentos errors
                  if (!transformed.exportaciones[exportIndex].partidas[pIndex].documentos) {
                    transformed.exportaciones[exportIndex].partidas[pIndex].documentos = [];
                  }
                  
                  // Process each documento error in the array
                  partidaError.documentos.forEach((documentoError, dIndex) => {
                    // Only process non-null error objects (null means no error at this index)
                    if (documentoError !== null && typeof documentoError === 'object') {
                      // Ensure documentos array is large enough to accommodate this index
                      while (transformed.exportaciones[exportIndex].partidas[pIndex].documentos.length <= dIndex) {
                        transformed.exportaciones[exportIndex].partidas[pIndex].documentos.push(null);
                      }
                      // Merge documento errors if there's already an object at this index
                      if (transformed.exportaciones[exportIndex].partidas[pIndex].documentos[dIndex] && 
                          typeof transformed.exportaciones[exportIndex].partidas[pIndex].documentos[dIndex] === 'object') {
                        Object.assign(transformed.exportaciones[exportIndex].partidas[pIndex].documentos[dIndex], documentoError);
                      } else {
                        // Set the documento error object at the correct index
                        transformed.exportaciones[exportIndex].partidas[pIndex].documentos[dIndex] = documentoError;
                      }
                    }
                  });
                }
              }
            });
          }
        }
      });
      
      processedErrors = transformed;
    }

    // Check if this is a simple error object (API error format)
    const isSimpleErrorObject = processedErrors && 
      typeof processedErrors === 'object' && 
      !Array.isArray(processedErrors) &&
      (processedErrors.status === 'error' || processedErrors.message || processedErrors.code) &&
      !processedErrors.common && 
      !processedErrors.exportaciones &&
      !isNewErrorFormat;

    // Optimized: Extract and group all errors by export number in a single pass
    const extractErrorsByExport = (errors) => {
      if (!errors || !Array.isArray(errors)) return {};
      
      const errorsByExport = {};
      
      errors.forEach((errorObj) => {
        if (!errorObj || typeof errorObj !== 'object') return;
        
        const rowIndex = readRowIndex(errorObj);
        const exportIndex = rowIndex !== undefined ? rowIndex : 0;
        const exportNumber = exportIndex + 1;
        
        if (!errorsByExport[exportNumber]) {
          errorsByExport[exportNumber] = {
            clientes: [],
            exportLevel: [], // Direct export-level errors (codigo_un, medio_transporte_frontera, etc.)
            partidas: [], // Direct partida-level errors (masa_bruta, codigo_nc, etc.)
            documentos: [], // All documento field errors (codigo_documento, fecha, numero, tipo, etc.)
            embalajes: []
          };
        }
        
        // Extract clientes errors
        if (errorObj.clientes && typeof errorObj.clientes === 'object') {
          Object.entries(errorObj.clientes).forEach(([field, errorMessage]) => {
            if (errorMessage && typeof errorMessage === 'string') {
              errorsByExport[exportNumber].clientes.push({
                field,
                errorMessage
              });
            }
          });
        }
        
        // Extract direct declaration-level errors (codigo_un, medio_transporte_frontera, etc.)
        const rowDeclErrors = readRowDeclarationErrors(errorObj);
        if (rowDeclErrors && typeof rowDeclErrors === 'object') {
          Object.entries(rowDeclErrors).forEach(([field, errorMessage]) => {
            // Skip partidas (handled separately) and nested objects
            if (field !== 'partidas' && typeof errorMessage === 'string') {
              errorsByExport[exportNumber].exportLevel.push({
                field,
                errorMessage
              });
            }
          });
        }

        // Extract errors from partidas (direct partida errors, documentos, and embalajes)
        if (rowDeclErrors?.partidas && Array.isArray(rowDeclErrors.partidas)) {
          rowDeclErrors.partidas.forEach((partida, pIndex) => {
            // Skip null entries
            if (!partida || typeof partida !== 'object') return;
            
            // Extract direct partida-level errors (masa_bruta, codigo_nc, etc.)
            Object.entries(partida).forEach(([field, errorMessage]) => {
              // Skip nested objects (documentos, embalajes) - handled separately
              if (field !== 'documentos' && field !== 'embalajes' && typeof errorMessage === 'string') {
                errorsByExport[exportNumber].partidas.push({
                  partidaIndex: pIndex,
                  field,
                  errorMessage
                });
              }
            });
            
            // Extract all documento field errors (codigo_documento, fecha, numero, tipo, etc.)
            if (partida.documentos && Array.isArray(partida.documentos)) {
              partida.documentos.forEach((documento, dIndex) => {
                if (documento !== null && typeof documento === 'object') {
                  Object.entries(documento).forEach(([field, errorMessage]) => {
                    if (errorMessage && typeof errorMessage === 'string') {
                      errorsByExport[exportNumber].documentos.push({
                        partidaIndex: pIndex,
                        documentoIndex: dIndex,
                        field,
                        errorMessage
                      });
                    }
                  });
                }
              });
            }
            
            // Extract embalajes (package) errors
            if (partida.embalajes && Array.isArray(partida.embalajes)) {
              partida.embalajes.forEach((embalaje, eIndex) => {
                if (embalaje !== null && typeof embalaje === 'object') {
                  Object.entries(embalaje).forEach(([field, errorMessage]) => {
                    if (errorMessage && typeof errorMessage === 'string') {
                      errorsByExport[exportNumber].embalajes.push({
                        partidaIndex: pIndex,
                        embalajeIndex: eIndex,
                        field,
                        errorMessage
                      });
                    }
                  });
                }
              });
            }
          });
        }
      });
      
      return errorsByExport;
    };

    // Extract all errors grouped by export number
    const errorsByExport = isNewErrorFormat ? extractErrorsByExport(taricJsonErrors.errors) : {};
    const exportNumbers = Object.keys(errorsByExport).map(Number).sort((a, b) => a - b);

    // CRITICAL: Always render if taricJsonErrors exists (even if empty object) hasTaricJsonErrors check is done in parent component
    return(
        <>
            {location.pathname === "/" && taricJsonErrors !== null && taricJsonErrors !== undefined && (
              <div className="flex flex-col flex-1 min-h-0 overflow-hidden rounded-lg border-containera">
                <div className="flex-1 min-h-0 overflow-y-auto">
                {/* Simple Error Object (API error format) */}
                {isSimpleErrorObject && (
                  <div className="mb-6">
                    <h2 className="font-semibold text mb-2 text-red-600">
                      {getTaricJsonErrorsTranslation('taricGenerationError', isSpanish)}
                    </h2>
                    <div className="">
                      {taricJsonErrors.message && (
                        <p className="text-xs mb-2 text-red-700">
                          {taricJsonErrors.message}
                        </p>
                      )}
                    </div>
                  </div>
                )}

                {/* Show message from new error format */}
                {isNewErrorFormat && taricJsonErrors.message && (
                  <div className="mb-6">
                    <h2 className="font-semibold text mb-2 text-red-600">
                      {getTaricJsonErrorsTranslation('taricGenerationError', isSpanish)}
                    </h2>
                    <div className="">
                      <p className="text-xs mb-2 text-red-700">
                        {taricJsonErrors.message}
                      </p>
                    </div>
                  </div>
                )}

                {/* Optimized: Unified error display grouped by export number */}
                {exportNumbers.length > 0 && (
                  <div className="mb-6">
                    {exportNumbers.map((exportNumber) => {
                      const exportErrors = errorsByExport[exportNumber];
                      if (!exportErrors || (
                        exportErrors.clientes.length === 0 && 
                        exportErrors.exportLevel.length === 0 &&
                        exportErrors.partidas.length === 0 &&
                        exportErrors.documentos.length === 0 && 
                        exportErrors.embalajes.length === 0
                      )) {
                        return null;
                      }

                      const declIndex = exportNumber - 1;

                      return (
                        <div key={exportNumber} className="mb-4">
                          <h3 className="font-semibold text-base mb-2 text-gray-700 ml-4">
                            {getTaricJsonErrorsTranslation('export', isSpanish)} #{exportNumber}
                          </h3>
                          
                          {/* Client Errors */}
                          {exportErrors.clientes.length > 0 && (
                            <div className="mb-3">
                              <h4 className="font-medium text-sm mb-2 text-gray-600 ml-4">
                                {getTaricJsonErrorsTranslation('clientErrors', isSpanish)}
                              </h4>
                              <div className="space-y-2">
                                {exportErrors.clientes.map((error, idx) => {
                                  const receiverFieldId = getReviewReceiverFieldId(error.field, exportNumber);
                                  const actualFieldName = extractFieldName(receiverFieldId);
                                  return (
                                    <div
                                      key={idx}
                                      className="p-3 px-3 rounded-lg text-sm break-words bg-red-50 cursor-pointer hover:opacity-90 transition-opacity"
                                      onClick={(e) => handleClickWithExpand(e, receiverFieldId, declIndex)}
                                    >
                                      <div className="flex-1 min-w-0 flex flex-col-reverse">
                                        <Link
                                          to={receiverFieldId}
                                          onClick={(e) => handleClickWithExpand(e, receiverFieldId, declIndex)}
                                          className="break-words text-gray-700 text-xs"
                                        >
                                          {error.errorMessage}
                                        </Link>
                                        <div className="flex gap-2">
                                          <div className="text-xs text-gray-600 font-semibold truncate">
                                            {getFieldDisplayName(error.field)}
                                          </div>
                                          <div className="text-[8px] text-gray-500 font-mono break-all truncate">
                                            {actualFieldName}
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          )}
                          
                          {/* Export Level Errors (codigo_un, medio_transporte_frontera, etc.) */}
                          {exportErrors.exportLevel.length > 0 && (
                            <div className="mb-3">
                              <h4 className="font-medium text-sm mb-2 text-gray-600 ml-4">
                                {getTaricJsonErrorsTranslation('exportErrors', isSpanish)}
                              </h4>
                              <div className="space-y-2">
                                {exportErrors.exportLevel.map((error, idx) => {
                                  const fieldId = `${error.field}_${exportNumber}`;
                                  const actualFieldName = extractFieldName(fieldId);
                                  return (
                                    <div
                                      key={idx}
                                      className="p-3 px-3 rounded-lg text-sm break-words bg-red-50 cursor-pointer hover:opacity-90 transition-opacity"
                                      onClick={(e) => handleClickWithExpand(e, fieldId, declIndex)}
                                    >
                                      <div className="flex-1 min-w-0 flex flex-col-reverse">
                                        <Link
                                          to={fieldId}
                                          onClick={(e) => handleClickWithExpand(e, fieldId, declIndex)}
                                          className="break-words text-gray-700 text-xs"
                                        >
                                          {error.errorMessage}
                                        </Link>
                                        <div className="flex gap-2">
                                          <div className="text-xs text-gray-600 font-semibold truncate">
                                            {getFieldDisplayName(error.field)}
                                          </div>
                                          <div className="text-[8px] text-gray-500 font-mono break-all truncate">
                                            {actualFieldName}
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          )}
                          
                          {/* Direct Partida Errors (masa_bruta, codigo_nc, etc.) - Group by partida */}
                          {exportErrors.partidas.length > 0 && (
                            <div className="ml-4 mb-3">
                              {Object.entries(
                                exportErrors.partidas.reduce((acc, error) => {
                                  const key = error.partidaIndex;
                                  if (!acc[key]) acc[key] = [];
                                  acc[key].push(error);
                                  return acc;
                                }, {})
                              ).map(([partidaIndex, partidaErrors]) => (
                                <div key={partidaIndex} className="mb-3">
                                  <h4 className="font-medium text-sm mb-2 text-gray-600 ml-4">
                                    {getTaricJsonErrorsTranslation('item', isSpanish)} #{parseInt(partidaIndex) + 1}
                                  </h4>
                                  <div className="space-y-2 ml-4">
                                    {partidaErrors.map((error, idx) => {
                                      const fieldId = `${error.field}_${exportNumber}_${error.partidaIndex}`;
                                      const actualFieldName = extractFieldName(fieldId);
                                      return (
                                        <div
                                          key={idx}
                                          className="p-3 px-3 rounded-lg text-sm break-words bg-red-50 cursor-pointer hover:opacity-90 transition-opacity"
                                          onClick={(e) => handleClickWithExpand(e, fieldId, declIndex)}
                                        >
                                          <div className="flex-1 min-w-0 flex flex-col-reverse">
                                            <Link
                                              to={fieldId}
                                              onClick={(e) => handleClickWithExpand(e, fieldId, declIndex)}
                                              className="break-words text-gray-700 text-xs"
                                            >
                                              {error.errorMessage}
                                            </Link>
                                            <div className="flex gap-2">
                                              <div className="text-xs text-gray-600 font-semibold truncate">
                                                {getFieldDisplayName(error.field)}
                                              </div>
                                              <div className="text-[8px] text-gray-500 font-mono break-all truncate">
                                                {actualFieldName}
                                              </div>
                                            </div>
                                          </div>
                                        </div>
                                      );
                                    })}
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                          
                          {/* Documento Errors - Group by partida and documento */}
                          {exportErrors.documentos.length > 0 && (
                            <div className="ml-4 mb-3">
                              {Object.entries(
                                exportErrors.documentos.reduce((acc, error) => {
                                  const key = `${error.partidaIndex}_${error.documentoIndex}`;
                                  if (!acc[key]) acc[key] = [];
                                  acc[key].push(error);
                                  return acc;
                                }, {})
                              ).map(([key, documentoErrors]) => {
                                const [partidaIndex, documentoIndex] = key.split('_');
                                return (
                                  <div key={key} className="mb-3">
                                    <h4 className="font-medium text-sm mb-2 text-gray-600 ml-4">
                                      {getTaricJsonErrorsTranslation('item', isSpanish)} #{parseInt(partidaIndex) + 1}
                                    </h4>
                                    <div className="ml-4 mb-2">
                                      <h5 className="font-medium text-xs mb-1 text-gray-500 ml-4">
                                        {getTaricJsonErrorsTranslation('document', isSpanish)} #{parseInt(documentoIndex) + 1}
                                      </h5>
                                      <div className="space-y-2 ml-4">
                                        {documentoErrors.map((error, idx) => {
                                          const fieldId = `documents_${error.field}_${exportNumber}_${error.partidaIndex}_${error.documentoIndex}`;
                                          const actualFieldName = extractFieldName(fieldId);
                                          return (
                                            <div
                                              key={idx}
                                              className="p-3 px-3 rounded-lg text-sm break-words bg-red-50 cursor-pointer hover:opacity-90 transition-opacity"
                                              onClick={(e) => handleClickWithExpand(e, fieldId, declIndex)}
                                            >
                                              <div className="flex-1 min-w-0 flex flex-col-reverse">
                                                <Link
                                                  to={fieldId}
                                                  onClick={(e) => handleClickWithExpand(e, fieldId, declIndex)}
                                                  className="break-words text-gray-700 text-xs"
                                                >
                                                  {error.errorMessage}
                                                </Link>
                                                <div className="flex gap-2">
                                                  <div className="text-xs text-gray-600 font-semibold truncate">
                                                    {getFieldDisplayName(error.field)}
                                                  </div>
                                                  <div className="text-[8px] text-gray-500 font-mono break-all truncate">
                                                    {actualFieldName}
                                                  </div>
                                                </div>
                                              </div>
                                            </div>
                                          );
                                        })}
                                      </div>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          )}
                          
                          {/* Embalajes (Package) Errors - Group by partida */}
                          {exportErrors.embalajes.length > 0 && (
                            <div className="ml-4 mb-3">
                              {Object.entries(
                                exportErrors.embalajes.reduce((acc, error) => {
                                  const key = error.partidaIndex;
                                  if (!acc[key]) acc[key] = [];
                                  acc[key].push(error);
                                  return acc;
                                }, {})
                              ).map(([partidaIndex, partidaErrors]) => (
                                <div key={partidaIndex} className="mb-3">
                                  <h4 className="font-medium text-sm mb-2 text-gray-600 ml-4">
                                    {getTaricJsonErrorsTranslation('item', isSpanish)} #{parseInt(partidaIndex) + 1}
                                  </h4>
                                  <div className="ml-4 mb-2">
                                    <h5 className="font-medium text-xs mb-1 text-gray-500 ml-4">
                                      {getTaricJsonErrorsTranslation('package', isSpanish)}
                                    </h5>
                                    {Object.entries(
                                      partidaErrors.reduce((acc, error) => {
                                        const key = error.embalajeIndex;
                                        if (!acc[key]) acc[key] = [];
                                        acc[key].push(error);
                                        return acc;
                                      }, {})
                                    ).map(([embalajeIndex, embalajeErrors]) => (
                                      <div key={embalajeIndex} className="ml-4 mb-2">
                                        <p className="text-xs font-medium mb-1 text-gray-500">
                                          {getTaricJsonErrorsTranslation('package', isSpanish)} #{parseInt(embalajeIndex) + 1}
                                        </p>
                                        <div className="space-y-2">
                                          {embalajeErrors.map((error, idx) => {
                                            const fieldId = `packages_${error.field}_${exportNumber}_${error.partidaIndex}_${error.embalajeIndex}`;
                                            const actualFieldName = extractFieldName(fieldId);
                                            return (
                                              <div
                                                key={idx}
                                                className="p-3 px-3 rounded-lg text-sm break-words bg-red-50 cursor-pointer hover:opacity-90 transition-opacity"
                                                onClick={(e) => handleClickWithExpand(e, fieldId, declIndex)}
                                              >
                                                <div className="flex-1 min-w-0 flex flex-col-reverse">
                                                  <Link
                                                    to={fieldId}
                                                    onClick={(e) => handleClickWithExpand(e, fieldId, declIndex)}
                                                    className="break-words text-gray-700 text-xs"
                                                  >
                                                    {error.errorMessage}
                                                  </Link>
                                                  <div className="flex gap-2">
                                                    <div className="text-xs text-gray-600 font-semibold truncate">
                                                      {getFieldDisplayName(error.field)}
                                                    </div>
                                                    <div className="text-[8px] text-gray-500 font-mono break-all truncate">
                                                      {actualFieldName}
                                                    </div>
                                                  </div>
                                                </div>
                                              </div>
                                            );
                                          })}
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Fallback: Show if new format has errors but nothing displayed yet */}
                {isNewErrorFormat && exportNumbers.length === 0 && !isSimpleErrorObject && (
                  <div className="mb-6">
                    <h2 className="font-semibold text mb-2 text-red-600">
                      {getTaricJsonErrorsTranslation('taricGenerationError', isSpanish)}
                    </h2>
                    <p className="text-xs mb-2 text-red-700">
                      {isSpanish ? 'Hay errores de validación. Por favor, revise los detalles.' : 'There are validation errors. Please check the details.'}
                    </p>
                    {/* Debug: Show raw errors count */}
                    {process.env.NODE_ENV === 'development' && (
                      <p className="text-xs text-gray-500">
                        Debug: {taricJsonErrors.errors?.length || 0} error(s) found
                      </p>
                    )}
                  </div>
                )}

                {/* Always show transformed errors if they exist (fallback to old format) */}
                {!isNewErrorFormat && !isSimpleErrorObject && processedErrors?.exportaciones && processedErrors.exportaciones.length > 0 && (
                  <div className="mb-6">
                    <h2 className="font-semibold text mb-2 text-red-600">
                      {getTaricJsonErrorsTranslation('exportErrors', isSpanish)}
                    </h2>
                    <p className="text-xs mb-2 text-red-700">
                      {isSpanish ? 'Hay errores de validación. Por favor, revise los detalles.' : 'There are validation errors. Please check the details.'}
                    </p>
                  </div>
                )}
                
                {/* Common Errors Section */}
                {!isSimpleErrorObject && processedErrors.common && Object.keys(processedErrors.common).length > 0 && (
                  <div className="mb-6">
                    <h2 className="font-semibold text mb-2 text-red-600">
                      {getTaricJsonErrorsTranslation('commonErrors', isSpanish)}
                    </h2>
                    
                    {/* Client Errors */}
                    {processedErrors.common.clientes && Object.keys(processedErrors.common.clientes).length > 0 && (
                      <div className="mb-4">
                        <h3 className="font-medium text-sm mb-2 text-gray-600">
                          {getTaricJsonErrorsTranslation('clientErrors', isSpanish)}
                        </h3>
                        <div className="space-y-2">
                          {Object.entries(convertErrorsToNewFormat(processedErrors.common.clientes)).map(([fieldKey, errorObj]) => {
                            const receiverFieldId = getReviewReceiverFieldId(errorObj.field, 1);
                            const actualFieldName = extractFieldName(receiverFieldId);
                            return (
                              <div
                                key={fieldKey}
                                className="p-3 px-3 rounded-lg text-sm break-words bg-red-50 cursor-pointer hover:opacity-90 transition-opacity"
                                onClick={(e) => handleClickWithExpand(e, receiverFieldId, 0)}
                              >
                                <div className="flex-1 min-w-0 flex flex-col-reverse">
                                  <Link
                                    to={receiverFieldId}
                                    onClick={(e) => handleClickWithExpand(e, receiverFieldId, 0)}
                                    className="break-words text-gray-700 text-xs"
                                  >
                                    {errorObj.errorMessage}
                                  </Link>
                                  <div className="flex gap-2">
                                    <div className="text-xs text-gray-600 font-semibold truncate">
                                      {errorObj.errorField}
                                    </div>
                                    <div className="text-[8px] text-gray-500 font-mono break-all truncate">
                                      {actualFieldName}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {/* Agent Errors */}
                    {processedErrors.common.agentes && Object.keys(processedErrors.common.agentes).length > 0 && (
                      <div className="mb-4">
                        <h3 className="font-medium text-sm mb-2 text-gray-600">
                          {getTaricJsonErrorsTranslation('agentErrors', isSpanish)}
                        </h3>
                        <div className="space-y-2">
                          {Object.entries(convertErrorsToNewFormat(processedErrors.common.agentes)).map(([fieldKey, errorObj]) => {
                            const actualFieldName = extractFieldName(errorObj.field);
                            return (
                              <div
                                key={fieldKey}
                                className="p-3 px-3 rounded-lg text-sm break-words bg-red-50 cursor-pointer hover:opacity-90 transition-opacity"
                                onClick={(e) => handleClickWithExpand(e, errorObj.field, 0)}
                              >
                                <div className="flex-1 min-w-0 flex flex-col-reverse">
                                  <Link
                                    to={errorObj.field}
                                    onClick={(e) => handleClickWithExpand(e, errorObj.field, 0)}
                                    className="break-words text-gray-700 text-xs"
                                  >
                                    {errorObj.errorMessage}
                                  </Link>
                                  <div className="flex gap-2">
                                    <div className="text-xs text-gray-600 font-semibold truncate">
                                      {errorObj.errorField}
                                    </div>
                                    <div className="text-[8px] text-gray-500 font-mono break-all truncate">
                                      {actualFieldName}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Export Errors Section - Only show if we haven't already shown direct errors */}
                {/* Skip this section if we've already displayed errors directly from new format */}
                {!isSimpleErrorObject && 
                 processedErrors?.exportaciones && 
                 processedErrors.exportaciones.length > 0 && 
                 exportNumbers.length === 0 && (
                  <div>
                    {/* <h2 className="font-semibold text mb-3 text-red-600 text-sm">
                      {getTaricJsonErrorsTranslation('exportErrors', isSpanish)}
                    </h2> */}
                    {processedErrors.exportaciones.map((exportError, errorIndex) => {
                      // Check if export has any errors (direct, clientes, or in partidas)
                      const exportHasDirectErrors = hasDirectErrors(exportError);
                      const exportHasClientesErrors = exportError?.clientes && Object.keys(exportError.clientes).length > 0;
                      const exportHasPartidaErrors = exportError?.partidas && Array.isArray(exportError.partidas) && exportError.partidas.some(partida => partida !== null && partidaHasErrors(partida));
                      
                      // Only show export if it has errors
                      if (!exportHasDirectErrors && !exportHasClientesErrors && !exportHasPartidaErrors) {
                        return null;
                      }

                      // Determine the actual export number: 1. Use _exportNumber if available (from new format transformation) - this is the most reliable 2. Use exportError.id if available (from backend) 3. Try to match error to declaration by expediente or other unique fields 4. If errors array length matches declarations length, assume same order 5. Fall back to errorIndex + 1 as last resort
                      let exportNumber = errorIndex + 1;
                      
                      if (exportError._exportNumber !== undefined && exportError._exportNumber !== null) {
                        // New format: use the stored export number from exportacionesIndex
                        exportNumber = exportError._exportNumber;
                      } else if (exportError.id !== undefined && exportError.id !== null) {
                        // Backend provided the export ID - use it
                        exportNumber = exportError.id;
                      } else if (declarations.length > 0) {
                        // Try to match by expediente or other identifying fields
                        const matchingDeclarationIndex = declarations.findIndex((decl, idx) => {
                          // Match by expediente if available in error
                          if (exportError.expediente && decl.expediente === exportError.expediente) {
                            return true;
                          }
                          // If errors array has same length as declarations, assume same order
                          if (processedErrors.exportaciones.length === declarations.length) {
                            return idx === errorIndex;
                          }
                          return false;
                        });
                        
                        if (matchingDeclarationIndex !== -1) {
                          exportNumber = matchingDeclarationIndex + 1;
                        } else if (processedErrors.exportaciones.length === declarations.length) {
                          // Same length - assume same order
                          exportNumber = errorIndex + 1;
                        }
                      }

                      const declIndexFallback = Math.max(0, exportNumber - 1);

                      return (
                        <div key={errorIndex} className="mb-4">
                          <h3 className="font-semibold text-base mb-2 text-gray-700 ml-4">
                            {getTaricJsonErrorsTranslation('export', isSpanish)} #{exportNumber}
                          </h3>
                          
                          {/* Client Errors for this export (from new format) - Skip if already shown directly */}
                          {exportError.clientes && 
                           Object.keys(exportError.clientes).length > 0 && 
                           exportNumbers.length === 0 && (
                            <div className="mb-3">
                              <h4 className="font-medium text-sm mb-2 text-gray-600 ml-4">
                                {getTaricJsonErrorsTranslation('clientErrors', isSpanish)}
                              </h4>
                              <div className="space-y-2">
                                {Object.entries(convertErrorsToNewFormat(exportError.clientes)).map(([fieldKey, errorObj]) => {
                                  const receiverFieldId = getReviewReceiverFieldId(errorObj.field, exportNumber);
                                  const actualFieldName = extractFieldName(receiverFieldId);
                                  return (
                                    <div
                                      key={fieldKey}
                                      className="p-3 px-3 rounded-lg text-sm break-words bg-red-50 cursor-pointer hover:opacity-90 transition-opacity"
                                      onClick={(e) => handleClickWithExpand(e, receiverFieldId, declIndexFallback)}
                                    >
                                      <div className="flex-1 min-w-0 flex flex-col-reverse">
                                        <Link
                                          to={receiverFieldId}
                                          onClick={(e) => handleClickWithExpand(e, receiverFieldId, declIndexFallback)}
                                          className="break-words text-gray-700 text-xs"
                                        >
                                          {errorObj.errorMessage}
                                        </Link>
                                        <div className="flex gap-2">
                                          <div className="text-xs text-gray-600 font-semibold truncate">
                                            {errorObj.errorField}
                                          </div>
                                          <div className="text-[8px] text-gray-500 font-mono break-all truncate">
                                            {actualFieldName}
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          )}

                          {/* Export level errors */}
                          {exportHasDirectErrors && (
                            <div className="space-y-2 mb-3">
                              {Object.entries(convertErrorsToNewFormat(exportError)).map(([fieldKey, errorObj]) => {
                                // Skip partidas, clientes, and internal fields
                                if (fieldKey === 'partidas' || fieldKey === 'clientes' || fieldKey === '_exportNumber') return null;
                                const fieldId = `${errorObj.field}_${exportNumber}`;
                                const actualFieldName = extractFieldName(fieldId);
                                return (
                                  <div
                                    key={fieldKey}
                                    className="p-3 px-3 rounded-lg text-sm break-words bg-red-50 cursor-pointer hover:opacity-90 transition-opacity"
                                    onClick={(e) => handleClickWithExpand(e, fieldId, declIndexFallback)}
                                  >
                                    <div className="flex-1 min-w-0 flex flex-col-reverse">
                                      <Link
                                        to={fieldId}
                                        onClick={(e) => handleClickWithExpand(e, fieldId, declIndexFallback)}
                                        className="break-words text-gray-700 text-xs"
                                      >
                                        {errorObj.errorMessage}
                                      </Link>
                                      <div className="flex gap-2">
                                        <div className="text-xs text-gray-600 font-semibold truncate">
                                          {errorObj.errorField}
                                        </div>
                                        <div className="text-[8px] text-gray-500 font-mono break-all truncate">
                                          {actualFieldName}
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          )}

                          {/* Partidas Errors - Only show partidas that have errors */}
                          {exportError?.partidas && Array.isArray(exportError.partidas) && exportError.partidas.length > 0 && 
                            exportError.partidas.map((partida, pIndex) => {
                              // Skip null entries and only show partida if it has errors
                              if (!partida || !partidaHasErrors(partida)) {
                                return null;
                              }

                              return (
                                <div key={pIndex} className="ml-4 mb-3">
                                  <h4 className="font-medium text-sm mb-2 text-gray-600 ml-4">
                                    {getTaricJsonErrorsTranslation('item', isSpanish)} #{pIndex + 1}
                                  </h4>
                                  
                                  {/* Direct partida errors (like masa_bruta, etc.) */}
                                  {hasDirectErrors(partida) && (
                                    <div className="space-y-2 mb-2 ml-2">
                                      {Object.entries(convertErrorsToNewFormat(partida)).map(([fieldKey, errorObj]) => {
                                        // Skip nested objects like documentos, embalajes
                                        if (fieldKey === 'documentos' || fieldKey === 'embalajes') return null;
                                        const fieldId = `${errorObj.field}_${exportNumber}_${pIndex}`;
                                        const actualFieldName = extractFieldName(fieldId);
                                        return (
                                          <div
                                            key={fieldKey}
                                            className="p-3 px-3 rounded-lg text-sm break-words bg-red-50 cursor-pointer hover:opacity-90 transition-opacity"
                                            onClick={(e) => handleClickWithExpand(e, fieldId, declIndexFallback)}
                                          >
                                            <div className="flex-1 min-w-0 flex flex-col-reverse">
                                              <Link
                                                to={fieldId}
                                                onClick={(e) => handleClickWithExpand(e, fieldId, declIndexFallback)}
                                                className="break-words text-gray-700 text-xs"
                                              >
                                                {errorObj.errorMessage}
                                              </Link>
                                              <div className="flex gap-2">
                                                <div className="text-xs text-gray-600 font-semibold truncate">
                                                  {errorObj.errorField}
                                                </div>
                                                <div className="text-[8px] text-gray-500 font-mono break-all truncate">
                                                  {actualFieldName}
                                                </div>
                                              </div>
                                            </div>
                                          </div>
                                        );
                                      })}
                                    </div>
                                  )}
                                  
                                  {/* Embalajes Errors - Only show embalajes that have errors */}
                                  {partida?.embalajes && Array.isArray(partida.embalajes) && partida.embalajes.length > 0 && (
                                    <div className="ml-4 mb-2">
                                      <h5 className="font-medium text-xs mb-1 text-gray-500 ml-4">
                                        {getTaricJsonErrorsTranslation('package', isSpanish)}
                                      </h5>
                                      {partida.embalajes.map((embalaje, eIndex) => {
                                        // Skip null entries and only show embalaje if it has errors
                                        if (!embalaje || !hasDirectErrors(embalaje)) return null;
                                        
                                        return (
                                          <div key={eIndex} className="ml-4 mb-2">
                                            <p className="text-xs font-medium mb-1 text-gray-500">
                                              {getTaricJsonErrorsTranslation('package', isSpanish)} #{eIndex + 1}
                                            </p>
                                            <div className="space-y-2">
                                              {Object.entries(convertErrorsToNewFormat(embalaje)).map(([fieldKey, errorObj]) => {
                                                const fieldId = `packages_${errorObj.field}_${exportNumber}_${pIndex}_${eIndex}`;
                                                const actualFieldName = extractFieldName(fieldId);
                                                return (
                                                  <div
                                                    key={fieldKey}
                                                    className="p-3 px-3 rounded-lg text-sm break-words bg-red-50 cursor-pointer hover:opacity-90 transition-opacity"
                                                    onClick={(e) => handleClickWithExpand(e, fieldId, declIndexFallback)}
                                                  >
                                                    <div className="flex-1 min-w-0 flex flex-col-reverse">
                                                      <Link
                                                        to={fieldId}
                                                        onClick={(e) => handleClickWithExpand(e, fieldId, declIndexFallback)}
                                                        className="break-words text-gray-700 text-xs"
                                                      >
                                                        {errorObj.errorMessage}
                                                      </Link>
                                                      <div className="flex gap-2">
                                                        <div className="text-xs text-gray-600 font-semibold truncate">
                                                          {errorObj.errorField}
                                                        </div>
                                                        <div className="text-[8px] text-gray-500 font-mono break-all truncate">
                                                          {actualFieldName}
                                                        </div>
                                                      </div>
                                                    </div>
                                                  </div>
                                                );
                                              })}
                                            </div>
                                          </div>
                                        );
                                      })}
                                    </div>
                                  )}
                                  
                                  {/* Documentos Errors - Only show documentos that have errors */}
                                  {/* Skip if codigo_documento errors are already shown directly */}
                                  {partida?.documentos && 
                                   Array.isArray(partida.documentos) && 
                                   partida.documentos.length > 0 && 
                                   exportNumbers.length === 0 && (
                                    <div className="ml-4 mb-2">
                                      <h5 className="font-medium text-xs mb-1 text-gray-500 ml-4">
                                        {getTaricJsonErrorsTranslation('document', isSpanish)}
                                      </h5>
                                      {partida.documentos.map((documento, dIndex) => {
                                        // Skip null entries and only show documento if it has errors
                                        if (!documento || !hasDirectErrors(documento)) return null;
                                        
                                        return (
                                          <div key={dIndex} className="ml-4 mb-2">
                                            <p className="text-xs font-medium mb-1 text-gray-500">
                                              {getTaricJsonErrorsTranslation('document', isSpanish)} #{dIndex + 1}
                                            </p>
                                            <div className="space-y-2">
                                              {Object.entries(convertErrorsToNewFormat(documento)).map(([fieldKey, errorObj]) => {
                                                const fieldId = `documents_${errorObj.field}_${exportNumber}_${pIndex}_${dIndex}`;
                                                const actualFieldName = extractFieldName(fieldId);
                                                return (
                                                  <div
                                                    key={fieldKey}
                                                    className="p-3 px-3 rounded-lg text-sm break-words bg-red-50 cursor-pointer hover:opacity-90 transition-opacity"
                                                    onClick={(e) => handleClickWithExpand(e, fieldId, declIndexFallback)}
                                                  >
                                                    <div className="flex-1 min-w-0 flex flex-col-reverse">
                                                      <Link
                                                        to={fieldId}
                                                        onClick={(e) => handleClickWithExpand(e, fieldId, declIndexFallback)}
                                                        className="break-words text-gray-700 text-xs "
                                                      >
                                                        {errorObj.errorMessage}
                                                      </Link>
                                                      <div className="flex gap-2">
                                                        <div className="text-xs text-gray-600 font-semibold truncate">
                                                          {errorObj.errorField}
                                                        </div>
                                                        <div className="text-[8px] text-gray-500 font-mono break-all truncate">
                                                          {actualFieldName}
                                                        </div>
                                                      </div>
                                                    </div>
                                                  </div>
                                                );
                                              })}
                                            </div>
                                          </div>
                                        );
                                      })}
                                    </div>
                                  )}
                                </div>
                              );
                            })
                          }
                        </div>
                      );
                    })}
                  </div>
                )}
                </div>
              </div>
          )}
        </>
    )
}
    
const styles = `
  @keyframes highlight {
    0% { background-color: rgba(255, 0, 0, 0.2); }
    50% { background-color: rgba(255, 0, 0, 0.2); }
    100% { background-color: transparent; }
  }

  .highlight-error {
    animation: highlight 5s ease-in-out;
    border-radius: 4px;
  }
`;

const styleSheet = document.createElement("style");
styleSheet.innerText = styles;
document.head.appendChild(styleSheet);